import React, { createContext, useContext, useState, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { 
  checkmarkCircle, 
  closeCircle, 
  warning, 
  informationCircle,
  close 
} from 'ionicons/icons';

// Snackbar Component
interface IonicSnackbarProps {
  open: boolean;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  onClose?: () => void;
  duration?: number;
  position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const CustomSnackBar: React.FC<IonicSnackbarProps> = ({ 
  open, 
  message, 
  type = 'info', 
  onClose,
  duration = 5000,
  position = 'top'
}) => {
  const [isVisible, setIsVisible] = useState(open);

  useEffect(() => {
    setIsVisible(open);
    if (open && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const configs = {
    success: {
      gradient: 'linear-gradient(135deg, #05ACD9 0%, #10b981 100%)', // app blue-green
      icon: checkmarkCircle,
    },
    error: {
      gradient: 'linear-gradient(135deg, #F43F5E 0%, #DC2626 100%)', // app red
      icon: closeCircle,
    },
    warning: {
      gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)', // app yellow
      icon: warning,
    },
    info: {
      gradient: 'linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)', // app blue-neutral
      icon: informationCircle,
    }
  };

  const config = configs[type] || configs.info;

const positionStyles = {
    top: { top: 'calc(var(--ion-safe-area-top, 80px) + 16px)', left: '50%', transform: 'translateX(-50%)' },
    bottom: { bottom: 'var(--ion-safe-area-bottom, 20px)', left: '50%', transform: 'translateX(-50%)' },
    'top-right': { top: 'calc(var(--ion-safe-area-top, 20px) + 16px)', right: '16px' },
    'top-left': { top: 'calc(var(--ion-safe-area-top, 20px) + 16px)', left: '16px' },
    'bottom-right': { bottom: 'var(--ion-safe-area-bottom, 20px)', right: '16px' },
    'bottom-left': { bottom: 'var(--ion-safe-area-bottom, 20px)', left: '16px' }
};

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      ...positionStyles[position],
      width: 'calc(100% - 32px)',
      maxWidth: '500px',
      zIndex: 10000,
      animation: position.includes('top') ? 'slideDown 0.3s ease-out' : 'slideUp 0.3s ease-out',
    }}>
      <div style={{
        background: config.gradient,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <IonIcon icon={config.icon} style={{ fontSize: '32px', color: '#ffffff', flexShrink: 0 }} />
          <span style={{
            fontFamily: 'var(--ion-font-family)',
            fontSize: '14px',
            fontWeight: '500',
            color: '#ffffff',
            lineHeight: '1.5',
          }}>
            {message}
          </span>
        </div>
        <button onClick={handleClose} style={{
          marginLeft: '12px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          background: 'rgba(255, 255, 255, 0.2)',
          border: 'none',
          cursor: 'pointer',
          flexShrink: 0,
        }}>
          <IonIcon icon={close} style={{ fontSize: '20px', color: '#ffffff' }} />
        </button>
      </div>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Context
interface SnackbarContextType {
  showSnackbar: (
    message: string,
    type?: 'success' | 'error' | 'warning' | 'info',
    duration?: number,
    position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  ) => void;
  success: (message: string, duration?: number, position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left') => void;
  error: (message: string, duration?: number, position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left') => void;
  warning: (message: string, duration?: number, position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left') => void;
  info: (message: string, duration?: number, position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left') => void;
  hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

// Provider
export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info' as 'success' | 'error' | 'warning' | 'info',
    duration: 5000,
    position: 'top' as 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  });

  const showSnackbar = (
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    duration = 5000,
    position: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' = 'bottom'
  ) => {
    setSnackbar({ open: true, message, type, duration, position });
  };

  const hideSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const success = (
    message: string,
    duration?: number,
    position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  ) => 
    showSnackbar(message, 'success', duration, position);

  const error = (
    message: string,
    duration?: number,
    position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  ) => 
    showSnackbar(message, 'error', duration, position);
  
  const warning = (
    message: string,
    duration?: number,
    position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  ) => 
    showSnackbar(message, 'warning', duration, position);

  const info = (
    message: string,
    duration?: number,
    position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  ) => 
    showSnackbar(message, 'info', duration, position);

  return (
    <SnackbarContext.Provider value={{ showSnackbar, success, error, warning, info, hideSnackbar }}>
      {children}
      <CustomSnackBar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        duration={snackbar.duration}
        position={snackbar.position}
        onClose={hideSnackbar}
      />
    </SnackbarContext.Provider>
  );
};

// Hook
export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};